import { SBox, SDialog, SRow } from "@avalara/skylab-sdk/react";
import React from "react";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import { EcmsRecord } from "../../app/ecmsSlice";
import {
    DetailPair,
    DetailSection,
    displayValue,
    EM_DASH,
    formatDate,
    joinParts,
    StatItem,
    StatusTag,
} from "./ecmsDetailHelpers";

/**
 * Layout is fully Skylab, no companion CSS or inline styles: the SBox card provides the
 * summary panel's surface (background / border / radius), SRow/SCol handle the columns,
 * <hr> draws the section dividers, and utility classes (flex / pad / margin / text-*) the rest.
 * Presentation helpers are shared with EcmsCertificateDetailDialog via ./ecmsDetailHelpers.
 */

interface EcmsCustomerDetailDialogProps {
    /** Row whose details are shown; the component renders nothing when null. */
    record: EcmsRecord | null;
    open: boolean;
    onClose: () => void;
}

/**
 * Read-only "Customer details" view for an ECMS row, rendered inside a Skylab SDialog.
 * All data comes from the already-loaded {@link EcmsRecord}; the dialog never fetches.
 */
const EcmsCustomerDetailDialog: React.FC<EcmsCustomerDetailDialogProps> = ({
    record,
    open,
    onClose,
}) => {
    // No record means nothing to show — render nothing rather than an empty dialog shell.
    if (!record) {
        return null;
    }

    return (
        <SDialog
            open={open}
            id="ecms-customer-detail-dialog"
            aria-modal="true"
            onS-dismiss={onClose}
            data-testid={testIDs.ecms.customerDetailDialog}>
            <div slot="header">
                <h2 className="margin-none">Customer Information</h2>
            </div>
            <div slot="body">
                <SBox className="secondary margin-bottom-md">
                    <SRow>
                        <StatItem label="Certificate type">
                            {displayValue(record.exemptCertTypeId)}
                        </StatItem>
                        <StatItem label="Expires">
                            {formatDate(record.expiryDate, EM_DASH)}
                        </StatItem>
                        <StatItem label="Status">
                            <StatusTag value={record.exemptCertStatusId} />
                        </StatItem>
                    </SRow>
                </SBox>

                <DetailSection title="Customer information">
                    <DetailPair label="Customer name" value={displayValue(record.customerName)} />
                    <DetailPair label="Customer code" value={displayValue(record.customerCode)} />
                    <DetailPair label="Country" value={displayValue(record.country)} />
                    <DetailPair
                        label="Address"
                        value={joinParts([record.address1, record.address2, record.address3], ", ")}
                    />
                    <DetailPair label="City" value={displayValue(record.city)} />
                    <DetailPair label="Region" value={displayValue(record.region)} />
                    <DetailPair label="Postal code" value={displayValue(record.postalCode)} />
                </DetailSection>

                <DetailSection title="Certificate information" withDivider>
                    <DetailPair
                        label="Certificate type"
                        value={displayValue(record.exemptCertTypeId)}
                    />
                    <DetailPair
                        label="Issuing country"
                        value={displayValue(record.countryIssued)}
                    />
                    <DetailPair
                        label="Effective date"
                        value={formatDate(record.effectiveDate, "Not set")}
                    />
                    <DetailPair
                        label="Regions applicable"
                        value={displayValue(record.regionsApplicable)}
                    />
                </DetailSection>

                <DetailSection title="Exemption reason" withDivider>
                    <DetailPair label="Business type" value={displayValue(record.businessTypeId)} />
                    <DetailPair
                        label="Business type description"
                        value={displayValue(record.businessTypeDescription)}
                    />
                    <DetailPair
                        label="Exemption reason"
                        value={displayValue(record.exemptReasonId)}
                    />
                    <DetailPair
                        label="Exemption reason description"
                        value={displayValue(record.exemptReasonDescription)}
                    />
                </DetailSection>
            </div>
            <div slot="footer">
                <button type="button" className="primary" onClick={onClose}>
                    Close
                </button>
            </div>
        </SDialog>
    );
};

export default EcmsCustomerDetailDialog;
